import { Injectable } from '@angular/core';

export type LogFn = (msg: string) => void;

export interface GatekeeperOptions {
  ctx?: string;
  cooldownOnRejectMs?: number;   // défaut 2500ms pour user-rejected, 1000ms sinon
  timeoutMs?: number;            // défaut 30000ms
  log?: LogFn;
  preserveTokenOnSuccess?: boolean; // garder le token pour chaîner plusieurs popups
}

export type GateResultReason = 'success' | 'user-rejected' | 'timeout' | 'error';
export interface GateResult { ok: boolean; reason: GateResultReason; }

export interface GatekeeperReleaseInfo {
  reason: GateResultReason;
  ctx?: string;
  token?: string | null;
}

@Injectable({ providedIn: 'root' })
export class WalletGatekeeperService {
  private inFlight = false;                // mutex global
  private currentToken: string | null = null;
  private suppressUntil = 0;               // fenêtre de suppression après rejet/erreur
  private lastUserClickTs = 0;

  get walletRequestInFlight(): boolean { return this.inFlight; }
  get suppressedForMs(): number { return Math.max(0, this.suppressUntil - Date.now()); }

  /** À appeler *dans* un handler de clic utilisateur. */
  generateIntent(ctx?: string, log?: LogFn): string {
    this.lastUserClickTs = Date.now();
    const tok = `intent:${Date.now()}:${Math.random().toString(36).slice(2, 8)}:${ctx ?? 'app'}`;
    this.currentToken = tok;
    log?.(`[gate] intent GENERATED ctx=${ctx ?? '-'} token=${tok}`);
    return tok;
  }

  /** API simple (compat) — retourne juste ok / false */
  async requestWithIntent<T>(
    token: string,
    action: () => Promise<T> | T,
    opts?: GatekeeperOptions
  ): Promise<boolean> {
    const res = await this.requestWithIntentEx(token, action, opts);
    return res.ok;
  }

  /** API enrichie — remonte la raison (success | user-rejected | timeout | error) */
  async requestWithIntentEx<T>(
    token: string,
    action: () => Promise<T> | T,
    opts?: GatekeeperOptions
  ): Promise<GateResult> {
    const log = opts?.log;
    const ctx = opts?.ctx;

    if (!this.canOpen(token, log)) {
      log?.(`[gate] request DENIED ctx=${ctx ?? '-'} token=${token}`);
      return { ok: false, reason: 'error' }; // refus par gate
    }
    if (!this.takeLock(token, log)) {
      return { ok: false, reason: 'error' };
    }

    const timeoutMs = opts?.timeoutMs ?? 30_000;

    let settled = false;
    let timer: any;
    const TIMEOUT_SENTINEL = Symbol('timeout');

    try {
      // On prépare l'action et un timeout "gagnant" via Promise.race
      const actionPromise = Promise.resolve().then(() => action());
      const timeoutPromise = new Promise<symbol>(res => {
        timer = setTimeout(() => {
          log?.(`[gate] TIMEOUT after ${timeoutMs}ms ctx=${ctx ?? '-'} token=${token}`);
          // Libère le lock côté gate tout de suite (comme avant)
          this.release('timeout', ctx, token, log, /*preserveToken*/ false);
          res(TIMEOUT_SENTINEL);
        }, timeoutMs);
      });

      const winner = await Promise.race([actionPromise, timeoutPromise]);

      if (winner === TIMEOUT_SENTINEL) {
        // L'appelant récupère bien un "timeout"
        return { ok: false, reason: 'timeout' };
      }

      // L'action a fini avant le timeout
      settled = true;
      clearTimeout(timer);
      this.release('success', ctx, token, log, /*preserveToken*/ !!opts?.preserveTokenOnSuccess);
      return { ok: true, reason: 'success' };

    } catch (err: any) {
      settled = true;
      clearTimeout(timer);
      const rejected = this.isUserRejected(err);
      log?.(`[gate] ERROR rejected=${rejected} ctx=${ctx ?? '-'} token=${token} err=${err?.message ?? err}`);
      // Cooldown identique au comportement actuel
      this.suppressFor(rejected ? (opts?.cooldownOnRejectMs ?? 2500) : (opts?.cooldownOnRejectMs ?? 1000), log);
      this.release(rejected ? 'user-rejected' : 'error', ctx, token, log, /*preserveToken*/ false);
      return { ok: false, reason: rejected ? 'user-rejected' : 'error' };
    }
  }


  /** Appel “sécurisé” d’un provider.request (si tu veux l’utiliser ailleurs) */
  async safeProviderRequest(
    token: string,
    provider: any,
    request: { method: string; params?: any[] | object },
    opts?: GatekeeperOptions
  ): Promise<GateResult> {
    return this.requestWithIntentEx(token, async () => {
      return provider.request(request);
    }, { ...opts, ctx: (opts?.ctx ?? 'provider') + ':' + request.method });
  }

  // ------------------------------- internals -------------------------------

  private canOpen(token: string, log?: LogFn): boolean {
    const now = Date.now();
    if (now < this.suppressUntil) {
      log?.(`[gate] BLOCKED cooldown ${Math.ceil((this.suppressUntil - now) / 1000)}s`);
      return false;
    }
    if (this.inFlight) {
      log?.('[gate] BLOCKED inFlight=true');
      return false;
    }
    if (!token || token !== this.currentToken) {
      log?.('[gate] BLOCKED invalid/missing token');
      return false;
    }
    if (now - this.lastUserClickTs > 5000) {
      log?.('[gate] BLOCKED token stale (no recent user gesture)');
      return false;
    }
    return true;
  }

  private takeLock(token: string, log?: LogFn): boolean {
    if (!this.canOpen(token, log)) return false;
    this.inFlight = true;
    log?.(`[gate] LOCK taken token=${token}`);
    return true;
  }

  private release(reason: GateResultReason, ctx?: string, token?: string | null, log?: LogFn, preserveToken = false) {
    this.inFlight = false;
    if (!preserveToken) this.currentToken = null;
    log?.(`[gate] LOCK released reason=${reason} ctx=${ctx ?? '-'} token=${token ?? '-'} preserveToken=${preserveToken}`);
  }

  private suppressFor(ms: number, log?: LogFn) {
    this.suppressUntil = Date.now() + ms;
    log?.(`[gate] SUPPRESS for ${ms}ms`);
  }

  /** Détection standardisée du rejet utilisateur */
  private isUserRejected(e: any): boolean {
    const msg = (e?.message || e?.reason || e?.error?.message || '').toLowerCase();
    const code = e?.code ?? e?.error?.code;
    return (
      code === 4001 ||                       // EIP-1193 user rejected request
      code === 5000 ||                       // MetaMask mobile
      code === 'ACTION_REJECTED' ||          // ethers v6
      String(code).toLowerCase().includes('action_rejected') ||
      /denied|rejected|user denied|user rejected/.test(msg)
    );
  }
}
